/**
 * Hotcoin REST API Client
 * @see https://www.hotcoin.com/en_US/docs/
 */

import type {
  HotcoinDepthResponse,
  HotcoinKlinesResponse,
  HotcoinSymbolsResponse,
  HotcoinTickerAllResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

/**
 * Hotcoin REST API base URL.
 * @see https://www.hotcoin.com/en_US/docs/
 */
const BASE_URL = 'https://api.hotcoinfin.com';

/**
 * Hotcoin REST API endpoints.
 * @see https://www.hotcoin.com/en_US/docs/
 */
export const hotcoinRestEndpoints = {
  /** Get all trading pairs */
  symbols: `${BASE_URL}/v1/common/symbols`,
  /** Get all tickers */
  ticker: `${BASE_URL}/v1/market/ticker`,
  /** Get kline/candlestick data (step param in seconds) */
  klines: `${BASE_URL}/v1/ticker`,
  /** Get orderbook depth */
  depth: `${BASE_URL}/v1/depth`,
  /** Get recent trades */
  trades: `${BASE_URL}/v1/trade`,
} as const;

// ========================================
// Symbol Helpers
// ========================================

/**
 * Formats base and quote currencies into Hotcoin symbol format.
 * Hotcoin uses lowercase underscore format: btc_usdt
 *
 * @example
 * formatHotcoinSymbol('BTC', 'USDT') // 'btc_usdt'
 */
export const formatHotcoinSymbol = (base: string, quote: string): string => {
  return `${base.toLowerCase()}_${quote.toLowerCase()}`;
};

/**
 * Parses a Hotcoin symbol into base and quote currencies.
 *
 * @example
 * parseHotcoinSymbol('btc_usdt') // { base: 'btc', quote: 'usdt' }
 */
export const parseHotcoinSymbol = (symbol: string): { base: string; quote: string } => {
  const [base, quote] = symbol.toLowerCase().split('_');
  return { base: base || '', quote: quote || '' };
};

// ========================================
// REST API Functions
// ========================================

/**
 * Request parameters for Hotcoin ticker endpoint.
 */
export interface HotcoinTickerRequest {
  /** Trading pair symbol (e.g., btc_usdt) - required for single ticker */
  symbol: string;
}

/**
 * Response for single ticker request.
 */
export interface HotcoinTickerResponse {
  code: number;
  msg: string;
  data: {
    /** Symbol */
    symbol: string;
    /** Last traded price */
    last: string;
    /** Best buy price (bid) */
    buy: string;
    /** Best sell price (ask) */
    sell: string;
    /** 24h high price */
    high: string;
    /** 24h low price */
    low: string;
    /** 24h base volume */
    vol: string;
    [key: string]: unknown;
  };
}

/**
 * Fetches 24-hour ticker statistics for a specific symbol.
 * Use getHotcoinTickerAll() to fetch all tickers.
 * @see https://www.hotcoin.com/en_US/docs/#real-time-ticker-data
 */
export const getHotcoinTicker = async (
  params: HotcoinTickerRequest,
  options?: RestRequestOptions,
): Promise<HotcoinTickerResponse> => {
  const response = await get<HotcoinTickerResponse, HotcoinTickerRequest>({
    url: hotcoinRestEndpoints.ticker,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour ticker statistics for all symbols.
 * @see https://www.hotcoin.com/en_US/docs/#real-time-ticker-data
 */
export const getHotcoinTickerAll = async (
  options?: RestRequestOptions,
): Promise<HotcoinTickerAllResponse> => {
  const response = await get<HotcoinTickerAllResponse, undefined>({
    url: hotcoinRestEndpoints.ticker,
    options,
  });

  return response.data;
};

/**
 * Fetches all available trading pairs.
 * @see https://www.hotcoin.com/en_US/docs/#get-all-trade-pairs
 */
export const getHotcoinSymbols = async (
  options?: RestRequestOptions,
): Promise<HotcoinSymbolsResponse> => {
  const response = await get<HotcoinSymbolsResponse, undefined>({
    url: hotcoinRestEndpoints.symbols,
    options,
  });

  return response.data;
};

/**
 * Request parameters for Hotcoin depth endpoint.
 */
export interface HotcoinDepthRequest {
  /** Trading pair symbol (e.g., btc_usdt) */
  symbol: string;
  /** Depth level (optional, e.g., 20, 50, 100) */
  depth?: number;
}

/**
 * Fetches orderbook depth for a symbol.
 * @see https://www.hotcoin.com/en_US/docs/#get-deep-data
 */
export const getHotcoinDepth = async (
  params: HotcoinDepthRequest,
  options?: RestRequestOptions,
): Promise<HotcoinDepthResponse> => {
  const response = await get<HotcoinDepthResponse, HotcoinDepthRequest>({
    url: hotcoinRestEndpoints.depth,
    params,
    options,
  });

  return response.data;
};

/**
 * Request parameters for Hotcoin klines endpoint.
 */
export interface HotcoinKlinesRequest {
  /** Trading pair symbol (e.g., btc_usdt) */
  symbol: string;
  /** Kline interval in seconds (60, 300, 900, 1800, 3600, 86400, 604800, 2592000) */
  step: number;
  /** Number of records to return (optional, default 300) */
  size?: number;
}

/**
 * Fetches kline (candlestick) data for a symbol.
 * NOTE: Hotcoin kline timestamps are already in MILLISECONDS (do NOT multiply by 1000).
 * @see https://www.hotcoin.com/en_US/docs/#get-kline-data
 */
export const getHotcoinKlines = async (
  params: HotcoinKlinesRequest,
  options?: RestRequestOptions,
): Promise<HotcoinKlinesResponse> => {
  const response = await get<HotcoinKlinesResponse, HotcoinKlinesRequest>({
    url: hotcoinRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};

/**
 * Request parameters for Hotcoin trades endpoint.
 */
export interface HotcoinTradesRequest {
  /** Trading pair symbol (e.g., btc_usdt) */
  symbol: string;
  /** Number of records to return (optional, default 100) */
  count?: number;
}

/**
 * Fetches recent trades for a symbol.
 * @see https://www.hotcoin.com/en_US/docs/?#get-real-time-trade-data
 */
export const getHotcoinTrades = async (
  params: HotcoinTradesRequest,
  options?: RestRequestOptions,
): Promise<import('./types').HotcoinTradesResponse> => {
  const response = await get<import('./types').HotcoinTradesResponse, HotcoinTradesRequest>({
    url: hotcoinRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};
